import { Rpc } from '@lightprotocol/stateless.js';
import { PublicKey } from '@solana/web3.js';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import {
  compressedBalanceAtom,
  decompressedBalanceAtom,
  errorAtom,
} from '@claim/atoms';

import { getDecompressedBalance } from '../../utils/get-decompressed-balance';
import { getTotalCompressedBalance } from '../../utils/get-total-compressed-balance';

/**
 * Custom hook for fetching both compressed and decompressed token balances.
 *
 * This hook uses the provided RPC connection to retrieve balances based on the owner's public key and mint address.
 * It updates corresponding atoms for error, compressed balance, and decompressed balance.
 *
 * @returns An object containing the fetchBalance function.
 */
export const useFetchBalance = () => {
  const setError = useSetAtom(errorAtom);
  const setCompressedBalance = useSetAtom(compressedBalanceAtom);
  const setDecompressedBalance = useSetAtom(decompressedBalanceAtom);

  /**
   * Fetches both compressed and decompressed token balances for a given address and mint address.
   * Updates the corresponding atoms with the retrieved values.
   *
   * @param address - The owner's address.
   * @param mintAddress - The mint address of the token.
   * @param connection - The RPC connection instance.
   */
  const fetchBalance = useCallback(
    async (address: string, mintAddress: PublicKey, connection: Rpc) => {
      const publicKey = new PublicKey(address);
      if (!publicKey) return;

      try {
        const totalCompressedBalance = await getTotalCompressedBalance(
          publicKey,
          mintAddress,
          connection,
        );

        setCompressedBalance((prev) => ({
          ...prev,
          [address]: totalCompressedBalance,
        }));

        const totalDecompressedBalance = await getDecompressedBalance(
          publicKey,
          mintAddress,
          connection,
        );
        setDecompressedBalance(totalDecompressedBalance);
      } catch (err) {
        setError('Failed to fetch balances');
        console.error(err);
      }
    },
    [setCompressedBalance, setDecompressedBalance, setError],
  );

  return { fetchBalance };
};
